import React, { useEffect } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { TimeResponse } from '@api/ringil-api.v2.tsx';
import {
  formatDateTimeFromDayjsInTzWithLocale,
} from '@utils/dateWithTz.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgCargotype from '@components/ringil3/icons/Cargotype.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import InputDateAndTime from '@components/InputDateAndTime/InputDateAndTime.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface ComponentProps {
  dateTimeResponse: TimeResponse;
  utcDateTime: dayjs.Dayjs;
  userTimezoneId: string;
  remainingTimeInHours?: number;
  readonly?: boolean;
  onChanged: (dateTimeResponse: TimeResponse, utcDateTime: dayjs.Dayjs, timezoneId: string) => void;
  dataTestId?: string;
}

interface Form {
  dateTimeResponse: TimeResponse;
  utcDateTime: dayjs.Dayjs;
  timezoneId: string;
}

const DistributionValidTillTag: React.FC<ComponentProps> = ({
                                                              dateTimeResponse,
                                                              utcDateTime,
                                                              userTimezoneId,
                                                              onChanged,
                                                              readonly = false,
                                                              dataTestId,
                                                            }) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();

  const localeShort = i18n.language?.split('-')[0]?.toLowerCase() || 'en';
  const supportedLocales = ['en', 'cs', 'de', 'fr', 'hu', 'pl'];
  const dayjsLocale = supportedLocales.includes(localeShort) ? localeShort : 'en';

  const form = useForm<Form>(
    yup.object().shape({}),
    {
      dateTimeResponse,
      utcDateTime,
      timezoneId: userTimezoneId,
    }, d => save(d), false, false,
  );

  const save = (data: Form) => {
    onChanged(data.dateTimeResponse, data.utcDateTime, data.timezoneId);
  };

  const printValue = (utcDateTime: dayjs.Dayjs, timezoneId: string) => {
    if (isNullOrUndefined(utcDateTime) || isNullOrUndefined(timezoneId)) {
      return null;
    }
    const timeLeft = dayjs().tz(timezoneId).locale(dayjsLocale).to(utcDateTime);
    return `${formatDateTimeFromDayjsInTzWithLocale(utcDateTime, timezoneId)} (${timeLeft}) \n${timezoneId}`;
  };

  return (
    <>
      <ButtonTag
        label={tr(`DistributionValidTillTag.title`, `Valid till`)}
        value={printValue(utcDateTime, userTimezoneId)}
        icon={<SvgCargotype />}
        actionIcon={<SvgTagEdit />}
        dataTestIdValue={dataTestId}
        onSave={onSuccess => {
          form.validateAndSend(onSuccess);
          onSuccess();
        }}
        readonly={readonly}
        popupWidth={'500px'}
        autoClose
      >
        <HorizontalFieldValue
          label={tr(`DistributionValidTillTag.validTill`, `Valid till`)}
          value={
            <InputDateAndTime
              timeZone={form.values.timezoneId}
              value={form.values.utcDateTime}
              onChange={(localDate, timezoneId) => {
                form.useProduce(draft => {
                  const tz = timezoneId || draft.timezoneId;
                  const inTz = tz ? (localDate as dayjs.Dayjs).tz(tz) : (localDate as dayjs.Dayjs);
                  draft.dateTimeResponse.localDate = inTz.format('YYYY-MM-DD');
                  draft.dateTimeResponse.hourString = inTz.format('HH:mm:ss');
                  draft.dateTimeResponse.timeZone = tz;
                  draft.utcDateTime = localDate as dayjs.Dayjs;
                  draft.timezoneId = tz;
                });
              }}
            />
          }
        />
      </ButtonTag>
    </>
  );
};

export default DistributionValidTillTag;
